/**
 * Repairs common broken markdown patterns from document chunking.
 * Uses heuristic pattern matching to detect and fix incomplete markdown syntax.
 */

export interface RepairOptions {
  /** Add visual hints about chunk boundaries and repairs */
  addChunkIndicators?: boolean;
  /** Try to repair tables vs removing them entirely */
  preservePartialTables?: boolean;
  /** Throw on unrecoverable patterns vs silent fallback */
  strictMode?: boolean;
}

export interface MarkdownIssue {
  type: 'table' | 'list' | 'heading' | 'link' | 'code' | 'unknown';
  line: number;
  original: string;
  repaired: string;
  severity: 'error' | 'warning' | 'info';
  description: string;
}

export interface RepairResult {
  /** Repaired markdown content ready for rendering */
  repairedContent: string;
  /** List of issues found and repaired */
  issues: MarkdownIssue[];
  /** Confidence level in repair quality */
  confidence: 'high' | 'medium' | 'low';
}

/**
 * Main repair function - orchestrates all repair strategies
 */
export function repairPartialMarkdown(
  content: string,
  options: RepairOptions = {},
): RepairResult {
  const {
    addChunkIndicators = true,
    preservePartialTables = true,
    strictMode = false,
  } = options;

  const issues: MarkdownIssue[] = [];
  let repairedContent = content;

  // Run repair strategies in order
  repairedContent = repairIncompleteCodeBlocks(repairedContent, issues, addChunkIndicators);
  repairedContent = repairPartialTables(
    repairedContent,
    issues,
    preservePartialTables,
    addChunkIndicators,
  );
  repairedContent = repairBrokenLists(repairedContent, issues, addChunkIndicators);
  repairedContent = repairBrokenLinks(repairedContent, issues);
  repairedContent = cleanEmptyHeadings(repairedContent, issues);

  // Determine confidence based on issues
  const confidence = calculateConfidence(issues);

  if (strictMode && confidence === 'low') {
    throw new Error('Markdown repair confidence too low');
  }

  return {
    repairedContent,
    issues,
    confidence,
  };
}

/**
 * Repairs incomplete code blocks by auto-closing them
 */
function repairIncompleteCodeBlocks(
  content: string,
  issues: MarkdownIssue[],
  addIndicator: boolean,
): string {
  const backtickMatches = content.match(/```/g) || [];

  // If odd number of triple backticks, code block is unclosed
  if (backtickMatches.length % 2 !== 0) {
    const lastBacktickIndex = content.lastIndexOf('```');
    const lineNumber = content.substring(0, lastBacktickIndex).split('\n').length;

    issues.push({
      type: 'code',
      line: lineNumber,
      original: content.substring(lastBacktickIndex),
      repaired: 'Added closing ```',
      severity: 'warning',
      description: 'Code block was not closed',
    });

    const indicator = addIndicator ? '\n*[Code block continues in next chunk]*' : '';
    return content + '\n```' + indicator;
  }

  return content;
}

/**
 * Repairs partial table rows and incomplete table structures
 * Also adds missing table headers for proper markdown table rendering
 */
function repairPartialTables(
  content: string,
  issues: MarkdownIssue[],
  preserve: boolean,
  addIndicator: boolean,
): string {
  const lines = content.split('\n');
  const repaired: string[] = [];
  let inTable = false;

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    const trimmed = line.trim();

    // Detect lines that start with | (potential table rows)
    if (trimmed.startsWith('|')) {
      const pipeCount = (line.match(/\|/g) || []).length;


      // Only flag as broken if VERY few pipes (likely not a table at all)
      // Example: "| 7." has only 1 pipe - clearly broken
      if (pipeCount <= 2) {
        issues.push({
          type: 'table',
          line: i + 1,
          original: line,
          repaired: 'Added context indicator',
          severity: 'warning',
          description: 'Incomplete table row detected',
        });

        if (addIndicator) {
          repaired.push('> *[Partial table content from document chunk]*');
        }
        repaired.push(line);
        inTable = false;
        continue;
      }

      // Check if this is the start of a table (first row with pipes)
      if (!inTable) {
        inTable = true;

        // Check if next line is a separator (|---|---|)
        // Must contain at least one dash to be a valid separator
        const nextLine = i + 1 < lines.length ? lines[i + 1].trim() : '';
        const isSeparator = /^\|[\s\-:]*\-[\s\-:]*\|/.test(nextLine);

        // If no separator, we need to add a header row and separator
        if (!isSeparator) {
          issues.push({
            type: 'table',
            line: i + 1,
            original: line,
            repaired: 'Added table header and separator',
            severity: 'info',
            description: 'Table missing header row - added generic headers',
          });

          if (addIndicator) {
            repaired.push('> *[Truncated table data]*');
            repaired.push(''); // Blank line to separate indicator from table
          }

          // Count columns from first data row
          const columnCount = pipeCount - 1;

          // Add generic header row
          const headers = Array(columnCount)
            .fill(0)
            .map((_, idx) => ` Col${idx + 1} `)
            .join('|');
          repaired.push(`|${headers}|`);

          // Add separator row
          const separator = Array(columnCount)
            .fill(0)
            .map(() => '---')
            .join('|');
          repaired.push(`|${separator}|`);
        }
      }

      // If missing closing pipe, add it
      if (!trimmed.endsWith('|')) {
        issues.push({
          type: 'table',
          line: i + 1,
          original: line,
          repaired: line + ' ... |',
          severity: 'info',
          description: 'Added missing closing pipe',
        });

        repaired.push(line + ' ... |');
        continue;
      }

      repaired.push(line);
      continue;
    } else {
      // Non-table line - reset table tracking
      inTable = false;
    }

    repaired.push(line);
  }

  return repaired.join('\n');
}

/**
 * Repairs orphaned numbered list items that don't start at 1
 */
function repairBrokenLists(
  content: string,
  issues: MarkdownIssue[],
  addIndicator: boolean,
): string {
  const lines = content.split('\n');
  const repaired: string[] = [];

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    const trimmed = line.trim();

    // Detect numbered list items
    const match = trimmed.match(/^(\d+)\.\s+(.+)$/);
    if (match) {
      const number = parseInt(match[1]);
      const text = match[2];

      // If number > 1 and not part of a properly started list (list must start at 1)
      if (number > 1 && !isPartOfProperList(lines, i)) {
        issues.push({
          type: 'list',
          line: i + 1,
          original: line,
          repaired: 'Converted to continuation paragraph',
          severity: 'warning',
          description: 'Orphaned numbered list item',
        });

        if (addIndicator) {
          repaired.push('> *[Continued from previous chunk]*');
        }
        repaired.push(`**${number}.** ${text}`);
        continue;
      }
    }

    repaired.push(line);
  }

  return repaired.join('\n');
}

/**
 * Checks if a line is part of a properly formed list (one that starts at 1)
 */
function isPartOfProperList(lines: string[], index: number): boolean {
  if (index === 0) return false;

  // Look backwards through the list to find where it starts
  let currentIndex = index;

  // Trace back through consecutive list items
  while (currentIndex > 0) {
    let foundPrevItem = false;

    // Check previous non-empty lines
    for (let i = currentIndex - 1; i >= 0; i--) {
      const prevLine = lines[i].trim();
      if (prevLine === '') continue;

      // Check if it's a numbered list item
      const prevMatch = prevLine.match(/^(\d+)\.\s+/);
      if (prevMatch) {
        const prevNum = parseInt(prevMatch[1]);

        // If we found item 1, this is a proper list
        if (prevNum === 1) return true;

        // Continue tracing backwards
        currentIndex = i;
        foundPrevItem = true;
        break;
      }

      // If previous line is not a list item, stop searching
      return false;
    }

    // If we didn't find a previous list item, stop
    if (!foundPrevItem) break;
  }

  return false;
}

/**
 * Repairs broken markdown links and images
 */
function repairBrokenLinks(content: string, issues: MarkdownIssue[]): string {
  let repaired = content;

  // Fix links with missing closing
  repaired = repaired.replace(/\[([^\]]+)\]\(\s*$/gm, (match, text) => {
    issues.push({
      type: 'link',
      line: 0, // Line number would need more context
      original: match,
      repaired: `[${text}](#incomplete-chunk "Link continues in next chunk")`,
      severity: 'info',
      description: 'Incomplete link syntax',
    });

    return `[${text}](#incomplete-chunk "Link continues in next chunk")`;
  });

  // Fix images with missing closing
  repaired = repaired.replace(/!\[([^\]]*)\]?\(?$/gm, (match, alt) => {
    issues.push({
      type: 'link',
      line: 0,
      original: match,
      repaired: `*[Image: ${alt}]*`,
      severity: 'info',
      description: 'Incomplete image syntax',
    });

    return `*[Image: ${alt}]*`;
  });

  return repaired;
}

/**
 * Repairs empty headings - merges with next line if it has content
 */
function cleanEmptyHeadings(content: string, issues: MarkdownIssue[]): string {
  const lines = content.split('\n');
  const repaired: string[] = [];

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    const trimmed = line.trim();

    // Detect empty headings (just # characters with no text)
    if (/^#{1,6}\s*$/.test(trimmed)) {
      // Check if next line has content that could be the heading text
      if (i + 1 < lines.length) {
        const nextLine = lines[i + 1].trim();

        // If next line has content and doesn't look like another markdown element
        if (nextLine && !nextLine.startsWith('#') && !nextLine.startsWith('|') &&
            !nextLine.match(/^\d+\.\s/) && !nextLine.startsWith('-') &&
            !nextLine.startsWith('*') && !nextLine.startsWith('>')) {
          // Merge heading marker with next line
          issues.push({
            type: 'heading',
            line: i + 1,
            original: line,
            repaired: `${trimmed} ${nextLine}`,
            severity: 'info',
            description: 'Merged empty heading with following line',
          });

          repaired.push(`${trimmed} ${nextLine}`);
          i++; // Skip the next line since we merged it
          continue;
        }
      }

      // If no content to merge with, keep the heading marker with a note
      issues.push({
        type: 'heading',
        line: i + 1,
        original: line,
        repaired: line,
        severity: 'info',
        description: 'Empty heading marker preserved',
      });
      repaired.push(line);
      continue;
    }

    repaired.push(line);
  }

  return repaired.join('\n');
}

/**
 * Calculates confidence score based on issues found
 */
function calculateConfidence(issues: MarkdownIssue[]): 'high' | 'medium' | 'low' {
  if (issues.length === 0) return 'high';

  const errorCount = issues.filter((i) => i.severity === 'error').length;
  const warningCount = issues.filter((i) => i.severity === 'warning').length;

  if (errorCount > 0) return 'low';
  if (warningCount > 2) return 'medium';

  return 'high';
}
